import { useEffect, useState } from 'react';
import { listDesignProjects, listDesignVersions } from '../../lib/designApi';
import './DesignHistoryDrawer.css';
import { toFa } from '../../utils/faFormat';

const MODE_FA = {
  greenfield: 'زمینهٔ خالی',
  olt_given: 'سایت داده‌شده',
  olt_odc_given: 'سایت + ODC داده‌شده',
  down_to_drop: 'تا لایهٔ دراپ',
};
const STATUS_FA = {
  draft: { t: 'پیش‌نویس', c: '#94a3b8' },
  running: { t: 'در حال اجرا', c: '#f59e0b' },
  complete: { t: 'کامل', c: '#34d399' },
  failed: { t: 'ناموفق', c: '#f87171' },
};

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'لحظاتی پیش';
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m.toLocaleString('fa-IR')} دقیقه پیش`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h.toLocaleString('fa-IR')} ساعت پیش`;
  const dd = Math.floor(h / 24);
  if (dd < 30) return `${dd.toLocaleString('fa-IR')} روز پیش`;
  return d.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
}
function absTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' })} ` +
         `${d.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}`;
}
function countPairs(rs) {
  const c = (rs && rs.counts) || {};
  return [['OLT', c.olt], ['ODC', c.odc], ['FAT', c.fat], ['HH', c.hh]];
}

export default function DesignHistoryDrawer({ open, onClose, onSelect, onSelectVersion, isMobile }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [expanded, setExpanded] = useState({});   // projectId -> bool
  const [versions, setVersions] = useState({});    // projectId -> version rows
  const [verLoading, setVerLoading] = useState({});
  const [verError, setVerError] = useState({});

  // Fetch the caller's working designs whenever the drawer opens.
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLoading(true); setError(null); setExpanded({}); setVersions({}); setVerError({});
    listDesignProjects().then((r) => {
      if (!alive) return;
      setLoading(false);
      if (r.error) setError(r.error);
      else setProjects(r.projects || []);
    });
    return () => { alive = false; };
  }, [open]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const toggleVersions = async (p) => {
    const id = p.id;
    const willOpen = !expanded[id];
    setExpanded((prev) => ({ ...prev, [id]: willOpen }));
    if (!willOpen || versions[id] || verLoading[id]) return;
    setVerLoading((prev) => ({ ...prev, [id]: true }));
    const r = await listDesignVersions(id);
    setVerLoading((prev) => ({ ...prev, [id]: false }));
    if (r.error) setVerError((prev) => ({ ...prev, [id]: r.error }));
    else setVersions((prev) => ({ ...prev, [id]: r.versions }));
  };

  if (!open) return null;
  const variant = isMobile ? 'sheet' : 'side';

  return (
    <div className={`dh-overlay ${variant}`} onClick={onClose}>
      <div className={`dh-panel ${variant}`} onClick={(e) => e.stopPropagation()}>
        {isMobile && <div className="dh-grab" />}
        <div className="dh-head">
          <div>
            <div className="dh-title">تاریخچهٔ طرح‌ها</div>
            {!loading && !error && (
              <div className="dh-sub">
                {projects.length > 0
                  ? `${projects.length.toLocaleString('fa-IR')} طرح ذخیره‌شده`
                  : 'بایگانیِ طراحی‌های شما'}
              </div>
            )}
          </div>
          <button type="button" className="dh-close" onClick={onClose} aria-label="بستن">×</button>
        </div>

        <div className="dh-body">
          {loading && [0, 1, 2].map((i) => <div key={i} className="dh-skel" />)}

          {!loading && error && <div className="dh-error">{error}</div>}

          {!loading && !error && projects.length === 0 && (
            <div className="dh-empty">
              هنوز طرحی ذخیره نشده است.<br />
              یک محدوده رسم کن و <b>«اجرای طراحی»</b> را بزن تا اینجا ظاهر شود.
            </div>
          )}

          {!loading && !error && projects.map((p) => {
            const st = STATUS_FA[p.status] || STATUS_FA.draft;
            const n = Number(p.snapshot_count || 0);
            return (
              <div key={p.id} className="dh-card">
                <button type="button" className="dh-row" style={{ '--st': st.c }} onClick={() => onSelect(p)}>
                  <div className="dh-row-top">
                    <span className="dh-name">{p.name || 'طرح بدون نام'}</span>
                    <span className="dh-badges">
                      <span className="dh-status"><span className="dh-dot" />{st.t}</span>
                      {p.output_pruned_at && <span className="dh-pruned">خروجی حذف شده</span>}
                    </span>
                  </div>
                  <div className="dh-meta">
                    <span>{MODE_FA[p.mode] || p.mode}</span>
                    {(p.design_profiles?.name || p.profile_name) && <span>پروفایل: {toFa(p.design_profiles?.name || p.profile_name)}</span>}
                    <span title={absTime(p.updated_at)}>{relTime(p.updated_at)}</span>
                  </div>
                  <div className="dh-chips">
                    {countPairs(p.result_summary).map(([k, val]) => (
                      <span key={k} className="dh-chip">{k} {Number(val || 0).toLocaleString('fa-IR')}</span>
                    ))}
                  </div>
                </button>

                {n > 0 && (
                  <button type="button" className={`dh-ver-toggle${expanded[p.id] ? ' open' : ''}`}
                    onClick={() => toggleVersions(p)}>
                    <span>نسخه‌ها ({n.toLocaleString('fa-IR')})</span>
                    <span className="dh-chev">▾</span>
                  </button>
                )}

                {expanded[p.id] && (
                  <div className="dh-versions">
                    {verLoading[p.id] && <div className="dh-ver-loading">در حال بارگذاری نسخه‌ها…</div>}
                    {verError[p.id] && <div className="dh-ver-error">{verError[p.id]}</div>}
                    {(versions[p.id] || []).map((v) => {
                      const vst = STATUS_FA[v.status] || STATUS_FA.complete;
                      const isWorking = !v.is_snapshot;
                      return (
                        <button key={v.id} type="button" className="dh-ver-row" style={{ '--st': vst.c }}
                          onClick={() => (isWorking ? onSelect(p) : onSelectVersion(v))}>
                          <span className="dh-ver-main">
                            <span className="dh-ver-label">
                              <span className="dh-dot" />
                              {isWorking ? 'نسخهٔ کاری' : `نسخهٔ ${Number(v.version).toLocaleString('fa-IR')}`}
                            </span>
                            {v.output_pruned_at && <span className="dh-pruned">خروجی حذف شده</span>}
                          </span>
                          <span className="dh-ver-time" title={absTime(v.updated_at)}>{relTime(v.updated_at)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
